// packages
import React from "react";
import { useFormContext, Controller } from "react-hook-form";
import { z } from "zod";
import Link from "next/link";

// utils
import Strings from "@/shared/utils/Strings.constants";
import { PublicConfig } from "@/shared/PublicConfig";
import { cn } from "../utils";

// components
import { FormField } from "@/shared/components/FormField";
import { InputWithLabel } from "@/shared/components/ui/InputWithLabel";
import GiftCardFieldWrapper from "@/shared/components/GiftCardFieldWrapper";
import { EmailSuggestionWrapper } from "./EmailSuggestionWrapper";
import { ZipSuggestionsPopup } from "./ZipAutocomplete/ZipSuggestionsPopup";

// hooks
import { useEmailSuggestion } from "../hooks/useEmailSuggestion";
import { useZipCombobox } from "./ZipAutocomplete/useZipCombobox";
import { useZipVerification } from "../hooks/useZipVerification";

// types
import { UsPolicyStepSchema } from "@/quote-ptz-us/src/schema/PtzUsQuote";
import { PolicyStepSchema } from "@/quote-ptz-ca/src/schema/PtzCaQuote";

type FloatingVariant = "floating" | "floating-inner";

/**
 * UserInfoEditor — slimmed-down editor used inside <UserModal /> (step 2 edit-user flow).
 *
 * Intentionally separate from <UserContactEditor /> so the step-2 "account exists" surface
 * (inline message below the email) is decoupled from step-1's <ExistingPolicyModal /> +
 * <PolicyErrorController /> async-error pipeline. account-exists state is owned by the
 * parent (UserModal) via local React state, never via appState.asyncErrors, so the two
 * flows can't overwrite each other.
 */
export interface UserInfoEditorProps {
    config: {
        stepSchema: typeof UsPolicyStepSchema | typeof PolicyStepSchema;
        floatingVariant?: FloatingVariant;
        firstNameField?: { label: string; placeholder?: string };
        lastNameField?: { label: string; placeholder?: string };
        emailField?: { label: string; placeholder?: string };
        zipField: {
            label: string;
            placeholder?: string;
            mask: (value: string) => string;
            showGiftCardWrapper?: boolean;
            /**
             * Statsig - zip code autocomplete test (ENG-16887). Same combobox + authoritative
             * validation as step 1, so the two zip inputs behave identically.
             */
            validateZipCode?: boolean;
            /** Hold /rewards/eligible until Spot confirms the zip exists. Not part of the experiment. */
            requireVerifiedZip?: boolean;
        };
    };
    styles?: {
        wrapper?: string;
        fields?: {
            firstName?: string;
            lastName?: string;
            email?: string;
            zipCode?: string;
        };
    };
    showAccountExistsError: boolean;
    onClearAccountExistsError: () => void;
    /**
     * Content rendered directly beneath the zip field. Used for breed re-selection when the
     * edited zip resolves to a state that doesn't offer a breed the customer already picked —
     * the fix belongs next to the field that caused it.
     *
     * Callers lay this form out as a grid with an explicit `order-*` per field, so the slot owns
     * its own layout classes: it needs a col-span and an order matching the zip's (equal order +
     * later DOM position lands it directly beneath), and must take no grid box when it renders
     * nothing, or an empty row's `gap` opens a hole under the zip.
     */
    afterZipSlot?: React.ReactNode;
}

export function UserInfoEditor(props: UserInfoEditorProps) {
    const { zipField, emailField, firstNameField, lastNameField, stepSchema } = props.config;
    const { styles, showAccountExistsError, onClearAccountExistsError, afterZipSlot } = props;
    const floatingVariant = props.config.floatingVariant ?? "floating";

    const {
        control,
        watch,
        setFocus,
        setValue,
        clearErrors,
        formState: { errors }
    } = useFormContext<z.infer<typeof stepSchema>>();

    const currentRatingZipcode = watch("ratingZipcode");
    const email = watch("email");
    const showGiftCardWrapper = !!zipField.showGiftCardWrapper;

    const { showSuggestion, suggestedEmail, handleSuggestionConfirm, handleSuggestionDismiss } = useEmailSuggestion(email ?? "");

    const validateZipCode = !!zipField.validateZipCode;
    const zipVerification = useZipVerification({ enabled: validateZipCode || !!zipField.requireVerifiedZip, zipDigits: currentRatingZipcode ?? "" });

    // Statsig - zip code autocomplete test start (ENG-16887)
    const zipCombobox = useZipCombobox({
        enabled: validateZipCode,
        verification: zipVerification,
        zipDigits: currentRatingZipcode ?? "",
        onCommit: zip => {
            setValue("ratingZipcode", zipField.mask(zip), { shouldDirty: true });
            // A picked row is a known-good zip, and RHF runs in onSubmit mode, so nothing else
            // would clear a manual error (the Save gate's) from a previous value.
            clearErrors("ratingZipcode");
        }
    });
    // test end

    return (
        <div className={cn("flex flex-col gap-6", styles?.wrapper)}>
            <Controller
                name="ratingZipcode"
                control={control}
                render={({ field: { ref, value, onChange, ...rest }, fieldState }) => (
                    <div className={cn(styles?.fields?.zipCode)}>
                        <GiftCardFieldWrapper
                            zipCode={currentRatingZipcode ?? ""}
                            showGiftCardWrapper={showGiftCardWrapper}
                            cssWrapper={styles?.fields?.zipCode}
                            isZipVerified={zipVerification.isVerified}
                            isZipVerifying={zipVerification.isVerifying}
                            isZipInvalid={zipVerification.isConfirmedInvalid}
                        >
                            <FormField className="flex-1" error={fieldState?.error?.message ?? zipVerification.inlineError} errorId="error-ratingZipcode">
                                {/* Statsig - zip code autocomplete test (ENG-16887): the popup anchors to
                                this wrapper, not FormField, so the error hint cannot displace it. */}
                                <div className="relative">
                                    <InputWithLabel
                                        label={zipField?.label || Strings.ZIP_CODE}
                                        variant={floatingVariant}
                                        inputRef={ref}
                                        error={fieldState?.error?.message ?? zipVerification.inlineError}
                                        inputProps={{
                                            ...rest,
                                            autoComplete: "postal-code",
                                            "aria-describedby": errors?.ratingZipcode || zipVerification.inlineError ? "error-ratingZipcode" : undefined,
                                            placeholder: zipField?.placeholder ?? " ",
                                            value: zipField.mask(value ?? ""),
                                            ...zipCombobox.comboboxProps,
                                            onChange: e => {
                                                onChange(zipField.mask(e.target.value));
                                                // Editing invalidates UserModal's Save-time "invalid zip" error.
                                                if (validateZipCode || zipField.requireVerifiedZip) clearErrors("ratingZipcode");
                                                zipCombobox.handleChange(e);
                                            },
                                            onBlur: () => {
                                                rest.onBlur();
                                                zipVerification.handleBlur();
                                                zipCombobox.handleBlur();
                                            }
                                        }}
                                    />
                                    <ZipSuggestionsPopup {...zipCombobox.popup} onSelect={zipCombobox.selectSuggestion} />
                                </div>
                            </FormField>
                        </GiftCardFieldWrapper>
                    </div>
                )}
            />
            {afterZipSlot}
            <Controller
                name="email"
                control={control}
                render={({ field: { ref, onChange, ...rest }, fieldState }) => (
                    <EmailSuggestionWrapper
                        className={styles?.fields?.email}
                        showSuggestion={showSuggestion}
                        suggestedEmail={suggestedEmail}
                        onSuggestionConfirm={() => {
                            handleSuggestionConfirm(onChange);
                            setFocus("email");
                        }}
                        onSuggestionDismiss={() => {
                            handleSuggestionDismiss();
                            setFocus("email");
                        }}
                    >
                        <FormField className="flex-1" error={fieldState?.error?.message} errorId="error-email">
                            <InputWithLabel
                                label={emailField?.label ?? Strings.EMAIL_ADDRESS}
                                variant={floatingVariant}
                                inputRef={ref}
                                error={fieldState?.error?.message}
                                inputProps={{
                                    ...rest,
                                    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                                        if (showAccountExistsError) onClearAccountExistsError();
                                        onChange(event.target.value);
                                    },
                                    placeholder: emailField?.placeholder ?? " ",
                                    type: "email",
                                    autoComplete: "email",
                                    "aria-describedby": errors?.email ? "error-email" : undefined
                                }}
                            />
                            {showAccountExistsError && (
                                <div id="account-exists-inline" role="alert" className="mt-2 flex flex-col break-words text-start text-sm font-bold leading-5 text-content-brand">
                                    <span>Email already has a Spot policy.</span>
                                    <span>
                                        <Link href={PublicConfig.MEMBER_LOGIN_URL} target="_blank" className="font-medium underline">
                                            Log in to add a pet
                                        </Link>{" "}
                                        <span className="font-medium">or use a different email.</span>
                                    </span>
                                </div>
                            )}
                        </FormField>
                    </EmailSuggestionWrapper>
                )}
            />
            <Controller
                name="firstName"
                control={control}
                render={({ field: { ref, onChange, ...rest }, fieldState }) => (
                    <FormField className={cn("flex-1", styles?.fields?.firstName)} error={fieldState?.error?.message} errorId="error-firstName">
                        <InputWithLabel
                            label={firstNameField?.label ?? Strings.YOUR_FIRST_NAME}
                            variant={floatingVariant}
                            inputRef={ref}
                            error={fieldState?.error?.message}
                            inputProps={{
                                ...rest,
                                onChange,
                                autoComplete: "given-name",
                                placeholder: firstNameField?.placeholder ?? " ",
                                "aria-describedby": errors?.firstName ? "error-firstName" : undefined,
                                maxLength: 50
                            }}
                        />
                    </FormField>
                )}
            />
            <Controller
                name="lastName"
                control={control}
                render={({ field: { ref, onChange, ...rest }, fieldState }) => (
                    <FormField className={cn("flex-1", styles?.fields?.lastName)} error={fieldState?.error?.message} errorId="error-lastName">
                        <InputWithLabel
                            label={lastNameField?.label ?? Strings.YOUR_LAST_NAME}
                            variant={floatingVariant}
                            inputRef={ref}
                            error={fieldState?.error?.message}
                            inputProps={{
                                ...rest,
                                onChange,
                                autoComplete: "family-name",
                                placeholder: lastNameField?.placeholder ?? " ",
                                "aria-describedby": errors?.lastName ? "error-lastName" : undefined,
                                maxLength: 50
                            }}
                        />
                    </FormField>
                )}
            />
        </div>
    );
}
